import { PopperMenu } from '@kc-console/common-ui';
import { useScopedLinkBuilder } from '@kc-console/context';

import HoverMenuTree from './HoverMenuTree';

import type { HTMLAttributes, MouseEvent } from 'react';
import type { PopoverOrigin, UseHoverPopoverResponse } from '@kc-console/common-ui';
import type { Menus } from '@kc-console/context';

interface Props extends UseHoverPopoverResponse<HTMLLIElement> {
  handleMenuClick: () => void;
  menus: Array<Menus>;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
}

/**
 * PopperMenu의 기본 list slot(MenuList)을 대체하는 래퍼.
 * @description
 * - MenuList는 children을 `<ul>`로 감싸고 `autoFocusItem`으로 포커스를 가져간다.
 * - HoverMenuTree는 `<div>` 기반 트리이며 hover 메뉴는 포커스를 빼앗지 않아야 하므로,
 *   MenuList 전용 prop(autoFocusItem)을 흡수하고 나머지를 `<div>`로 전달한다.
 */
const HoverMenuListSlot = ({
  autoFocusItem: _autoFocusItem,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { autoFocusItem?: boolean }) => <div {...rest} />;

const MenuItemHoverPopover = ({
  anchorEl,
  open,
  id,
  menus,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'right',
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'center',
  },
  onClose,
  onPopoverMouseEnter,
  onPopoverMouseLeave,
  handleMenuClick,
}: Props) => {
  const { getProps } = useScopedLinkBuilder();
  // TODO(navigation): HoverMenuTree 가 anchor item 렌더를 지원하도록 개선되면
  //   isAnchor 옵션 제거하고 href 로 전환. 현재 isAnchor:false 는 ctrl/cmd+click 새 탭만
  //   emulate 하며 shift→새 창, alt→다운로드, middle-click 은 지원하지 못한다.
  const handleClickServiceMenu = (menu: Menus, event: MouseEvent<HTMLLIElement>) => {
    const { onClick } = getProps(menu.path, { isAnchor: false, onClick: handleMenuClick });
    onClick(event);
  };

  // paper는 portal되지만 React 트리상 anchor(li)의 하위이므로, paper→anchor 이동 시
  // li.onMouseEnter가 발생하지 않아 닫기 타이머가 취소되지 않는다.
  // relatedTarget이 anchor 내부면 닫기를 건너뛰어 hover를 유지한다.
  const handlePopoverMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
    const related = event.relatedTarget;
    if (related instanceof Node && anchorEl?.contains(related)) {
      return;
    }
    onPopoverMouseLeave();
  };

  return (
    <PopperMenu
      id={id}
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      onClose={(_event, reason) => {
        // hover 메뉴는 hover-leave 타이머로만 닫는다.
        // click-outside(backdropClick)로 닫으면, 닫히는 순간 anchor 행의 즐겨찾기 버튼이
        // visibility:hidden 처리되어 mouseup 시 클릭이 anchor 링크로 새어나가 의도치 않은 네비게이션이 발생한다.
        if (reason === 'backdropClick') return;
        onClose();
      }}
      slots={{ list: HoverMenuListSlot }}
      slotProps={{
        // anchor와 popover 사이의 hover 이벤트를 매끄럽게 연결하기 위해 root의 pointerEvents를 해제하고,
        // 실제 컨텐츠인 paper에서만 다시 활성화한다.
        root: {
          sx: {
            pointerEvents: 'none',
          },
        },
        paper: {
          onMouseEnter: onPopoverMouseEnter,
          onMouseLeave: handlePopoverMouseLeave,
          sx: {
            pointerEvents: 'auto',
            margin: '0px 6px 0px -6px',
            padding: '8px 0',
            backgroundColor: theme => theme.color['base/white'],
            boxShadow: theme => theme.elevation.level_2,
          },
        },
      }}
    >
      <HoverMenuTree menus={menus} onClick={handleClickServiceMenu} />
    </PopperMenu>
  );
};

export default MenuItemHoverPopover;
